import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  COURSE_PRICE_NUM,
  SUMMER_COUPON_CODE,
  SUMMER_COUPON_DISCOUNT,
  BATCH_DURATION,
  BATCH_SESSIONS,
  BATCH_MODE,
  BATCH_SIZE,
} from '../constants';

// Helper — mirrors fmt() in RegistrationPage
function fmt(num) {
  return '₹' + num.toLocaleString('en-IN');
}

const COURSE_PRICE = fmt(COURSE_PRICE_NUM);
const DISCOUNT_PCT = Math.round(SUMMER_COUPON_DISCOUNT * 100);

const PLANS = [
  {
    id: 'python',
    icon: <img src="/python.png" alt="Python" style={{ height: 32, marginBottom: 4 }} />,
    label: 'Python + AI Course',
    subtitle: '6–8 Weeks · Beginner · Online Live',
    price: COURSE_PRICE,
    tag: 'Most Popular 🌟',
    bg: 'linear-gradient(135deg, #1B3A6B, #2A5298)',
    features: [
      '6–8 Week Program',
      'Live Online Sessions',
      'Project-Based Learning',
      'Certificate Included',
      'WhatsApp Doubt Support',
    ],
  },
  {
    id: 'webdev',
    icon: '🌐',
    label: 'Web Development Course',
    subtitle: '6–8 Weeks · Beginner · Online Live',
    price: COURSE_PRICE,
    tag: '',
    bg: 'linear-gradient(135deg, #C41E3A, #e84363)',
    features: [
      '6–8 Week Program',
      'Live Online Sessions',
      'Build Real Websites',
      'Certificate Included',
      'WhatsApp Doubt Support',
    ],
  },
  {
    id: 'msoffice',
    icon: '💼',
    label: 'MS Office & AI Tools',
    subtitle: '6–8 Weeks · Beginner · Online Live',
    price: COURSE_PRICE,
    tag: '',
    bg: 'linear-gradient(135deg, #1a7a4a, #25a366)',
    features: [
      '6–8 Week Program',
      'Live Online Sessions',
      'Practical Office Skills',
      'Certificate Included',
      'WhatsApp Doubt Support',
    ],
  },
];

export default function FeesPage() {
  const navigate = useNavigate();

  return (
    <div style={{ paddingTop: 64 }}>

      {/* Header */}
      <section style={{ background: 'var(--cream)', padding: '60px 24px 48px', textAlign: 'center' }}>
        <div className="badge">💰 Transparent Pricing</div>
        <h1 className="section-title" style={{ textAlign: 'center' }}>Fees & Instructions</h1>
        <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
          Affordable, quality education with zero hidden charges. Register first and pay in the first 3 classes.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="section">
        <div className="container">

          {/* Course cards */}
          <div className="grid-3" style={{ marginBottom: 32 }}>
            {PLANS.map(({ id, icon, label, subtitle, price, tag, bg, features }) => (
              <div key={id} style={{
                background: bg, borderRadius: 24, padding: 36,
                color: '#fff', position: 'relative',
              }}>
                {tag && (
                  <div style={{
                    position: 'absolute', top: 16, right: 16,
                    background: 'rgba(255,255,255,0.22)',
                    padding: '4px 12px', borderRadius: 50,
                    fontSize: 12, fontWeight: 800, color: '#fff',
                  }}>
                    {tag}
                  </div>
                )}

                {/* Icon */}
                <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>

                <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4, marginRight: tag ? 100 : 0 }}>
                  {label}
                </h3>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 14, fontWeight: 600 }}>
                  {subtitle}
                </p>

                <div style={{ fontSize: 40, fontWeight: 900, margin: '0 0 22px' }}>{price}</div>

                {features.map(f => (
                  <div key={f} style={{ display: 'flex', gap: 8, marginBottom: 9, fontSize: 14, color: 'rgba(255,255,255,0.88)' }}>
                    <span style={{ fontWeight: 900, color: '#F0B429' }}>✓</span>{f}
                  </div>
                ))}

                <button
                  onClick={() => navigate('/register')}
                  style={{
                    width: '100%', marginTop: 24,
                    background: 'rgba(255,255,255,0.92)',
                    color: '#1a1a2e',
                    padding: '14px', border: 'none', borderRadius: 50,
                    fontWeight: 900, fontSize: 15, cursor: 'pointer',
                    fontFamily: 'Nunito, sans-serif',
                    transition: 'opacity 0.2s',
                  }}
                >
                  Register for Course →
                </button>
              </div>
            ))}
          </div>

          {/* Summer coupon callout */}
          <div style={{
            marginBottom: 48,
            background: 'linear-gradient(135deg, #fffbea, #fff4cc)',
            border: '2px solid var(--gold)',
            borderRadius: 20,
            padding: '24px 28px',
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 36 }}>🏷️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 900, fontSize: 16, color: 'var(--navy)', marginBottom: 4 }}>
                Summer Discount — {DISCOUNT_PCT}% Off!
              </div>
              <div style={{ color: 'var(--gray)', fontSize: 14, lineHeight: 1.65 }}>
                Use coupon code{' '}
                <span style={{
                  fontFamily: 'monospace', fontWeight: 800,
                  background: 'rgba(27,58,107,0.1)', padding: '2px 8px',
                  borderRadius: 6, color: 'var(--navy)', letterSpacing: 1,
                }}>
                  {SUMMER_COUPON_CODE}
                </span>{' '}
                at checkout on the registration form to get {DISCOUNT_PCT}% off any course fee.
              </div>
            </div>
            <button
              className="btn-gold"
              onClick={() => navigate('/register')}
              style={{ whiteSpace: 'nowrap' }}
            >
              Register & Apply Coupon →
            </button>
          </div>

          {/* Info Grid */}
          <div className="grid-2">

            {/* Requirements */}
            <div className="card">
              <h3 style={{ color: 'var(--navy)', fontWeight: 800, fontSize: 19, marginBottom: 22 }}>
                📋 Requirements to Join
              </h3>
              {[
                ['💻', 'Laptop Required', 'Students must have a working laptop. Desktop/tablet is not recommended for coding.'],
                ['🌐', 'Internet Connection', 'Minimum 5 Mbps stable internet for smooth live sessions.'],
                ['📖', 'Basic English', 'Understanding basic written English helps follow course material.'],
                ['⏰', 'Time Commitment', `Attend ${BATCH_SESSIONS} as scheduled. Recordings available if you miss a class.`],
                ['📧', 'Active Email', 'Required for account creation and receiving course resources.'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
                  <div style={{ fontSize: 22, minWidth: 28 }}>{icon}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 2 }}>{title}</div>
                    <div style={{ color: 'var(--gray)', fontSize: 13, lineHeight: 1.65 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Batch Details */}
            <div className="card">
              <h3 style={{ color: 'var(--navy)', fontWeight: 800, fontSize: 19, marginBottom: 22 }}>
                📅 Batch Details
              </h3>
              {[
                ['📆', 'Duration', BATCH_DURATION],
                ['🕐', 'Sessions', BATCH_SESSIONS],
                ['👥', 'Batch Size', BATCH_SIZE],
                ['💻', 'Mode', BATCH_MODE],
                ['📅', 'Batch Days', 'Mon, Wed & Fri — pick any date on registration'],
                ['☁️', 'Platform', 'Google Meet / Zoom (link shared on WhatsApp)'],
                ['📝', 'Assessment', 'Weekly assignments + Final capstone project'],
                ['🎓', 'Certificate', 'Issued after successful course completion'],
                ['💬', 'Doubt Support', 'WhatsApp group for doubts between sessions'],
              ].map(([icon, k, v]) => (
                <div key={k} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  padding: '11px 0', borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--gray)', fontSize: 14 }}>
                    <span>{icon}</span>{k}
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14, textAlign: 'right', maxWidth: 200 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Promise */}
          <div style={{
            marginTop: 36, background: 'var(--gold-light)', border: '2px solid var(--gold)',
            borderRadius: 20, padding: 28, textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🤝</div>
            <h3 style={{ color: 'var(--navy)', fontWeight: 900, fontSize: 18, marginBottom: 8 }}>
              Our Promise to You
            </h3>
            <p style={{ color: 'var(--gray)', fontSize: 15, lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>
              You can register first and attend initial classes before payment. We discuss enrollment only after you feel confident with the teaching quality. No hidden fees. No pressure.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: 'var(--navy)', padding: '64px 24px', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ color: '#fff', fontWeight: 900, fontSize: 28, marginBottom: 12 }}>
            Still have questions about fees?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 16, marginBottom: 32 }}>
            WhatsApp us — we'll answer within minutes.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-gold" onClick={() => navigate('/register')}>📅 Register for Course</button>
            <button className="btn-whatsapp" onClick={() => window.open(`https://wa.me/919000000000?text=${encodeURIComponent('Hi! I have a question about VRK Tech Academy fees.')}`, '_blank')}>
              💬 WhatsApp Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}